import get from "lodash/get";
import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import SubdivisionForm from "~/pages/Editor/EditorSubdivision/modals/SubdivisionForm";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { DeleteSubdivisionModal } from "~/pages/Editor/EditorSubdivision/modals/DeleteSubdivisionModal";
import { EDITOR_MANAGING_SUBDIVISION_OPTIONS } from "~/pages/Editor/EditorSubdivision/options";
import { useGetSubdivisionQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import { SubdivisionFilter } from "~/widgets/Filter";

import { getEditorSubdivisionColumns } from "./model/tableAdapter";

import s from "./EditorSubdivision.less";

const formId = "SubdivisionForm";

const EditorSubdivision = () => {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    code,
    name,
    status,
    parentSubdivision,
    openModal,
    currentId,
  } = useGetLocationData();

  const openAddModal = () => {
    setDataReplace("openModal", "Add");
  };

  const { handleCount, handlePage } = usePaginationParams();

  const closeModal = () => {
    setDataReplace("openModal", "", "currentId", "");
  };

  const { data, loading, refetch } = useGetSubdivisionQuery({
    variables: {
      code: code ? code : undefined,
      name: name ? name : undefined,
      status: status ? String(status) : undefined,
      parentId: parentSubdivision ? String(parentSubdivision) : undefined,
      page,
      count: count || 25,
    },
    fetchPolicy: "no-cache",
  });

  const subdivisions = get(data, "SubdivisionGetter.get.items", []).map(
    (el) => ({
      ...el,
      IncludedInDivisionName: el.parentId && el?.Parent?.name,
      status: t(`editorSubdivision.${el.status}`),
      createdAt: getFormatDate(el.createdAt),
    }),
  );
  const total = Number(get(data, "SubdivisionGetter.get.total", 0)) || 0;

  const subdivision = get(data, "SubdivisionGetter.get.items", []).find(
    (el) => el.id === currentId,
  );

  const columns = getEditorSubdivisionColumns(
    t,
    EDITOR_MANAGING_SUBDIVISION_OPTIONS,
  );

  const handleRowEdit = (record: (typeof subdivisions)[number]) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: (typeof subdivisions)[number]) => {
    setDataReplace("openModal", "Delete", "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("editorSubdivision.managing_subdivisions")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<SubdivisionFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("subdivisions.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={subdivisions}
        loading={loading}
        onRowEdit={
          hasPermission("subdivisions.edit") ? handleRowEdit : undefined
        }
        onRowDelete={
          hasPermission("subdivisions.delete") ? handleRowDelete : undefined
        }
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === "Add" || openModal === "Edit"}
        title={
          openModal === "Add"
            ? t("editorSubdivision.subdivision_add")
            : t("editorSubdivision.edit")
        }
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <SubdivisionForm
          refetch={refetch}
          setCloseModal={closeModal}
          subdivision={subdivision}
          formId={formId}
        />
      </Modal>

      <DeleteSubdivisionModal
        isOpen={openModal === "Delete"}
        handleCloseModal={closeModal}
        refetch={refetch}
      />
    </Wrapper.Content>
  );
};

export default EditorSubdivision;
